import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { Panel, btnPrimary, btnSecondary, inputCls } from "@/components/admin/ui";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { CustomerPicker } from "@/components/admin/billing";

const VEHICLES = [["business", "Business Class"], ["first", "First Class"], ["van", "VAN"], ["standard", "Standard Class"]];

export default function AdminBookingNew() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    pickup: "", dropoff: "", date: today, time: "09:00", trip_type: "one_way",
    duration_hours: 2, vehicle_category: "business", price_chf: "",
    stops: [], stops_on_return: false,
    passengers: 1, luggage: 0, flight_number: "", notes: "",
    first_name: "", last_name: "", email: "", phone: "",
    payment_status: "manual", send_confirmation: true,
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const addStop = () => setForm((f) => ({ ...f, stops: [...f.stops, { address: "", place: null, wait_minutes: 0 }] }));
  const removeStop = (i) => setForm((f) => ({ ...f, stops: f.stops.filter((_, j) => j !== i) }));
  const updateStop = (i, patch) => setForm((f) => ({ ...f, stops: f.stops.map((s, j) => j === i ? { ...s, ...patch } : s) }));
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    adminApi.get("/customers?per_page=100").then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        stops: form.trip_type === "hourly" ? [] : form.stops
          .filter((s) => (s.address || "").trim().length > 2)
          .map((s) => ({ address: s.address, place: s.place, wait_minutes: Number(s.wait_minutes) || 0 })),
        stops_on_return: form.trip_type === "round_trip" ? form.stops_on_return : false,
        price_chf: form.price_chf ? Number(form.price_chf) : null,
        duration_hours: Number(form.duration_hours),
        passengers: Number(form.passengers), luggage: Number(form.luggage),
        flight_number: form.flight_number || null, notes: form.notes || null,
        email: form.email || null,
        origin_url: window.location.origin,
      };
      const { data } = await adminApi.post("/bookings", payload);
      toast.success(`Réservation ${data.booking.tracking_code} créée`);
      navigate(`/admin/app/bookings/${data.booking.id}`);
    } catch (error) {
      toast.error("Création impossible", { description: apiError(error, "Vérifiez les champs.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="admin-booking-new">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Nouvelle réservation manuelle</h1>
      <p className="text-sm text-slate">Pour les demandes par téléphone, e-mail, hôtels ou conciergeries. Le prix est calculé automatiquement si laissé vide.</p>
      <form onSubmit={submit}>
        <Panel title="Trajet" testId="new-journey">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate">Départ *</label>
              <AddressAutocomplete value={form.pickup} testId="new-pickup" inputClass={`${inputCls} mt-1`}
                placeholder="Adresse de prise en charge" onChange={(v) => setForm((f) => ({ ...f, pickup: v }))} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate">Destination {form.trip_type === "hourly" ? "(optionnel à l’heure)" : "*"}</label>
              <AddressAutocomplete value={form.dropoff} testId="new-dropoff" inputClass={`${inputCls} mt-1`}
                placeholder="Adresse de destination" onChange={(v) => setForm((f) => ({ ...f, dropoff: v }))} />
            </div>
            {form.trip_type !== "hourly" && (
              <div className="space-y-2 sm:col-span-2" data-testid="new-stops">
                {form.stops.map((stop, i) => (
                  <div key={i} className="rounded-xl border border-ink/10 bg-cloud/40 p-2.5" data-testid={`new-stop-${i}`}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Arrêt {i + 1}</span>
                      <button type="button" onClick={() => removeStop(i)} data-testid={`new-stop-remove-${i}`}
                        className="text-xs text-slate transition-colors hover:text-red-600">Retirer</button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
                      <AddressAutocomplete value={stop.address} testId={`new-stop-address-${i}`} inputClass={inputCls}
                        placeholder="Adresse de l'arrêt"
                        onChange={(v) => updateStop(i, { address: v?.target ? v.target.value : v })}
                        onSelect={(place) => updateStop(i, { place })} />
                      <input type="number" min="0" step="5" value={stop.wait_minutes}
                        onChange={(e) => updateStop(i, { wait_minutes: Math.max(0, Number(e.target.value) || 0) })}
                        data-testid={`new-stop-wait-${i}`} className={inputCls} placeholder="Attente (min)" />
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button type="button" onClick={addStop} data-testid="new-add-stop"
                    className="rounded-full border border-dashed border-miles/50 px-3 py-1.5 text-xs font-semibold text-miles transition-colors hover:bg-miles/5">+ Ajouter un arrêt</button>
                  {form.stops.length > 0 && <span className="text-[11px] text-slate">Attente 90.-/h dès 15 min · 0-14 min offert</span>}
                </div>
                {form.trip_type === "round_trip" && form.stops.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-ink">
                    <input type="checkbox" checked={form.stops_on_return}
                      onChange={(e) => setForm((f) => ({ ...f, stops_on_return: e.target.checked }))}
                      data-testid="new-stops-on-return" className="h-4 w-4 rounded border-ink/20 text-miles focus:ring-miles" />
                    Arrêts aussi au retour
                  </label>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate">Type</label>
              <select value={form.trip_type} onChange={set("trip_type")} data-testid="new-trip-type" className={`${inputCls} mt-1`}>
                <option value="one_way">Aller simple</option>
                <option value="round_trip">Aller-retour</option>
                <option value="hourly">Mise à disposition</option>
              </select>
            </div>
            {form.trip_type === "hourly" && (
              <div>
                <label className="text-xs font-medium text-slate">Durée (h)</label>
                <input type="number" min="2" max="12" value={form.duration_hours} onChange={set("duration_hours")} data-testid="new-duration" className={`${inputCls} mt-1`} />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate">Date *</label>
              <input type="date" required value={form.date} onChange={set("date")} data-testid="new-date" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Heure *</label>
              <input type="time" required value={form.time} onChange={set("time")} data-testid="new-time" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Véhicule</label>
              <select value={form.vehicle_category} onChange={set("vehicle_category")} data-testid="new-vehicle" className={`${inputCls} mt-1`}>
                {VEHICLES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Prix CHF (vide = calcul OSRM)</label>
              <input type="number" step="0.05" min="0" value={form.price_chf} onChange={set("price_chf")} data-testid="new-price" className={`${inputCls} mt-1 font-mono`} placeholder="Auto" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Passagers</label>
              <input type="number" min="1" max="8" value={form.passengers} onChange={set("passengers")} data-testid="new-passengers" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Bagages</label>
              <input type="number" min="0" max="12" value={form.luggage} onChange={set("luggage")} data-testid="new-luggage" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Vol</label>
              <input value={form.flight_number} onChange={set("flight_number")} data-testid="new-flight" className={`${inputCls} mt-1 font-mono`} placeholder="LX 2805" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Notes</label>
              <input value={form.notes} onChange={set("notes")} data-testid="new-notes" className={`${inputCls} mt-1`} />
            </div>
          </div>
        </Panel>

        <Panel title="Client" testId="new-customer" className="mt-4">
          {customers.length > 0 && (
            <div className="mb-3">
              <label className="text-xs font-medium text-slate">Client existant</label>
              <CustomerPicker customers={customers} onPick={(c) => setForm((f) => ({
                ...f, first_name: c.first_name || f.first_name, last_name: c.last_name || f.last_name,
                email: c.email || "", phone: c.phone || "",
              }))} />
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-slate">Prénom *</label>
              <input required value={form.first_name} onChange={set("first_name")} data-testid="new-first-name" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Nom *</label>
              <input required value={form.last_name} onChange={set("last_name")} data-testid="new-last-name" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">Téléphone *</label>
              <input required value={form.phone} onChange={set("phone")} data-testid="new-phone" className={`${inputCls} mt-1 font-mono`} placeholder="+41 79 …" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate">E-mail</label>
              <input type="email" value={form.email} onChange={set("email")} data-testid="new-email" className={`${inputCls} mt-1`} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-6 border-t border-slate/10 pt-3">
            <div>
              <label className="text-xs font-medium text-slate">Paiement</label>
              <select value={form.payment_status} onChange={set("payment_status")} data-testid="new-payment-status" className={`${inputCls} mt-1 w-48`}>
                <option value="manual">Réglé hors plateforme</option>
                <option value="invoiced">Facturation</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.send_confirmation} onChange={set("send_confirmation")} data-testid="new-send-confirmation" className="h-4 w-4 accent-miles" />
              Envoyer l’e-mail de confirmation
            </label>
          </div>
        </Panel>

        <div className="mt-4 flex gap-3">
          <button type="submit" disabled={busy} data-testid="new-submit" className={btnPrimary}>
            {busy ? "Création…" : "Créer la réservation"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className={btnSecondary}>Annuler</button>
        </div>
      </form>
    </div>
  );
}
